import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

const daysOptions = [
  { label: 'Any Day (All days of the week)', value: 'any' },
  { label: 'Weekdays (Monday to Friday)', value: 'weekdays' },
  { label: 'Weekends (Saturday to Sunday)', value: 'weekends' },
  { label: 'Monday', value: 'monday' },
  { label: 'Tuesday', value: 'tuesday' },
  { label: 'Wednesday', value: 'wednesday' },
  { label: 'Thursday', value: 'thursday' },
  { label: 'Friday', value: 'friday' },
  { label: 'Saturday', value: 'saturday' },
  { label: 'Sunday', value: 'sunday' },
];

const WaitTimeSlotsDrawer = ({ isOpen, onClose, onSave, initialData }) => {
  const [timeSlots, setTimeSlots] = useState(initialData?.timeSlots || [{
    day: 'any',
    fromHour: '8',
    fromMinute: '00',
    fromPeriod: 'am',
    toHour: '5',
    toMinute: '00',
    toPeriod: 'pm'
  }]);

  const handleAddTimeSlot = () => {
    setTimeSlots([...timeSlots, {
      day: 'any',
      fromHour: '8',
      fromMinute: '00',
      fromPeriod: 'am',
      toHour: '5',
      toMinute: '00',
      toPeriod: 'pm'
    }]);
  };

  const handleRemoveTimeSlot = (index) => {
    setTimeSlots(timeSlots.filter((_, i) => i !== index));
  };

  const handleTimeSlotChange = (index, field, value) => {
    const updatedTimeSlots = timeSlots.map((slot, i) => {
      if (i === index) {
        return { ...slot, [field]: value };
      }
      return slot;
    });
    setTimeSlots(updatedTimeSlots);
  };

  const handleSave = () => {
    const getDisplayDay = (dayValue) => {
      switch(dayValue) {
        case 'any':
          return 'Any Day';
        case 'weekdays':
          return 'Weekdays';
        case 'weekends':
          return 'Weekends';
        default:
          return dayValue.charAt(0).toUpperCase() + dayValue.slice(1);
      }
    };

    onSave({
      timeSlots,
      displayText: timeSlots.map(slot => 
        `${getDisplayDay(slot.day)} ${slot.fromHour}:${slot.fromMinute}${slot.fromPeriod} - ${slot.toHour}:${slot.toMinute}${slot.toPeriod}`
      ).join(', ')
    });
    onClose();
  };

  return (
    <Dialog
      as="div"
      className="fixed inset-0 overflow-hidden z-50"
      open={isOpen}
      onClose={onClose}
    >
      <div className="absolute inset-0 overflow-hidden">
        <Dialog.Overlay className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

        <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
          <div className="relative w-[900px]">
            <div className="h-full flex flex-col bg-white shadow-xl">
              {/* Header */}
              <div className="px-4 py-6 bg-gray-50 sm:px-6">
                <div className="flex items-start justify-between space-x-3">
                  <div className="space-y-1">
                    <Dialog.Title className="text-lg font-medium text-gray-900">
                      Time Slots Configuration
                    </Dialog.Title>
                    <p className="text-sm text-gray-500">
                      Set the time slots when the journey should proceed
                    </p>
                  </div>
                  <div className="h-7 flex items-center">
                    <button
                      type="button"
                      className="text-gray-400 hover:text-gray-500"
                      onClick={onClose}
                    >
                      <span className="sr-only">Close panel</span>
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Main content */}
              <div className="flex-1 flex flex-col justify-between">
                <div className="px-4 sm:px-6 overflow-y-auto">
                  <div className="space-y-6 pt-6 pb-5">
                    {timeSlots.map((slot, index) => (
                      <div key={index} className="border rounded-lg p-4 overflow-y-auto relative">
                        {timeSlots.length > 1 && (
                          <button
                            onClick={() => handleRemoveTimeSlot(index)}
                            className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
                          >
                            <XMarkIcon className="h-5 w-5" />
                          </button>
                        )}
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Day</label>
                            <select
                              value={slot.day}
                              onChange={(e) => handleTimeSlotChange(index, 'day', e.target.value)}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[var(--brand-primary)] focus:ring-[var(--brand-primary)] sm:text-sm"
                            >
                              {daysOptions.map((day) => (
                                <option key={day.value} value={day.value}>
                                  {day.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="grid grid-cols-3 gap-2 items-end">
                            <div>
                              <label className="block text-sm font-medium text-gray-700">From</label>
                              <select
                                value={slot.fromHour}
                                onChange={(e) => handleTimeSlotChange(index, 'fromHour', e.target.value)}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[var(--brand-primary)] focus:ring-[var(--brand-primary)] sm:text-sm"
                              >
                                {Array.from({ length: 12 }, (_, i) => i + 1).map((hour) => (
                                  <option key={hour} value={hour}>{hour}</option>
                                ))}
                              </select>
                            </div>
                            <div className="mt-5">
                              <select
                                value={slot.fromMinute}
                                onChange={(e) => handleTimeSlotChange(index, 'fromMinute', e.target.value)}
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[var(--brand-primary)] focus:ring-[var(--brand-primary)] sm:text-sm"
                              >
                                <option value="00">00</option>
                                <option value="30">30</option>
                              </select>
                            </div>
                            <div className="mt-5">
                              <select
                                value={slot.fromPeriod}
                                onChange={(e) => handleTimeSlotChange(index, 'fromPeriod', e.target.value)}
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[var(--brand-primary)] focus:ring-[var(--brand-primary)] sm:text-sm"
                              >
                                <option value="am">AM</option>
                                <option value="pm">PM</option>
                              </select>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-2 items-end">
                            <div>
                              <label className="block text-sm font-medium text-gray-700">To</label>
                              <select
                                value={slot.toHour}
                                onChange={(e) => handleTimeSlotChange(index, 'toHour', e.target.value)}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[var(--brand-primary)] focus:ring-[var(--brand-primary)] sm:text-sm"
                              >
                                {Array.from({ length: 12 }, (_, i) => i + 1).map((hour) => (
                                  <option key={hour} value={hour}>{hour}</option>
                                ))}
                              </select>
                            </div>
                            <div className="mt-5">
                              <select
                                value={slot.toMinute}
                                onChange={(e) => handleTimeSlotChange(index, 'toMinute', e.target.value)}
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[var(--brand-primary)] focus:ring-[var(--brand-primary)] sm:text-sm"
                              >
                                <option value="00">00</option>
                                <option value="30">30</option>
                              </select>
                            </div>
                            <div className="mt-5">
                              <select
                                value={slot.toPeriod}
                                onChange={(e) => handleTimeSlotChange(index, 'toPeriod', e.target.value)}
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[var(--brand-primary)] focus:ring-[var(--brand-primary)] sm:text-sm"
                              >
                                <option value="am">AM</option>
                                <option value="pm">PM</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    {timeSlots.length < 3 && <button
                      type="button"
                      onClick={() => {if(timeSlots.length < 3) handleAddTimeSlot()}}
                      className="mt-4 w-44 rounded-md border border-[var(--brand-primary)] bg-white py-2 px-4 text-sm font-medium text-[var(--brand-primary)] shadow-sm hover:bg-gray-50"
                    >
                      Add Time Slot
                    </button>}
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex-shrink-0 px-4 py-4 flex justify-end space-x-4 bg-gray-50">
                  <button
                    type="button"
                    className="rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] focus:ring-offset-2"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="rounded-md border border-transparent bg-[var(--brand-primary)] py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-[#3599ab] focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] focus:ring-offset-2"
                    onClick={handleSave}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default WaitTimeSlotsDrawer; 